<template>
  <div class="page-thread">
    <div v-if="postsError" class="error-banner">
      {{ $t('default.discussions.load_error') }}
    </div>

    <div v-if="postsLoading && localPosts.length === 0" class="loading-state">
      <Loader />
    </div>

    <ul class="posts-list">
      <li
        v-for="post in localPosts"
        :key="post.id"
        class="post-item"
        :class="{ 'post-item--deleted': post.is_deleted, 'post-item--optimistic': post._optimistic }"
      >
        <template v-if="post.is_deleted">
          <div class="post-item__deleted">
            <em>{{ $t('default.discussions.post_deleted') }}</em>
          </div>
        </template>

        <template v-else>
          <div class="post-item__header">
            <div class="post-item__avatar">
              <img
                v-if="post.author && post.author.avatar_url"
                :src="post.author.avatar_url"
                :alt="post.author.full_name"
                class="avatar-img"
              />
              <div v-else class="avatar-initials">
                {{ authorInitials(post.author) }}
              </div>
            </div>

            <div class="post-item__author-info">
              <span class="post-item__author-name">
                {{ post.author ? post.author.full_name : $t('default.discussions.unknown_author') }}
              </span>
              <span class="post-item__timestamp">
                {{ formatDate(post.created) }}
                <span v-if="post.modified !== post.created" class="post-item__edited">
                  ({{ $t('default.discussions.edited') }})
                </span>
              </span>
            </div>

            <div v-if="canEdit(post)" class="post-item__actions">
              <button class="small" @click="startEdit(post)">
                {{ $t('default.discussions.edit') }}
              </button>
            </div>
          </div>

          <div v-if="editingPostId === post.id" class="post-item__edit">
            <PostComposer
              :initial-body="post.body"
              :submitting="submitting"
              :error="submitError"
              submit-label="Save"
              :show-cancel="true"
              @submit="submitEdit(post.id, $event)"
              @cancel="editingPostId = null"
            />
          </div>

          <div v-else class="post-item__body">
            <MarkdownRenderer :content="post.body" />
          </div>

          <div class="post-item__reactions">
            <ReactionBar :post-id="post.id" :thread-id="thread.id" />
          </div>
        </template>
      </li>
    </ul>

    <div v-if="postsNextPage" class="load-more">
      <button class="green" :disabled="postsLoading" @click="loadMore">
        {{ postsLoading ? $t('default.discussions.loading') : $t('default.discussions.load_more_posts') }}
      </button>
    </div>

    <div v-if="!thread.is_locked" class="reply-section">
      <PostComposer
        :submitting="submitting"
        :error="submitError"
        :submit-label="$t('default.discussions.post_reply')"
        @submit="submitReply"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from '@/axios-instance'
import MarkdownRenderer from '@/components/shared/MarkdownRenderer.vue'
import PostComposer from '@/components/discussions/PostComposer.vue'
import ReactionBar from '@/components/discussions/ReactionBar.vue'
import Loader from '@/components/Loader.vue'
import { stringToDatetime } from '@/datetimeUtils'

const EDIT_WINDOW_MS = 10 * 60 * 1000

const props = defineProps({
  thread: {
    type: Object,
    required: true,
  },
})

const store = useStore()
const currentUser = computed(() => store.state.user?.user)

const localPosts = ref([])
const postsLoading = ref(false)
const postsError = ref(false)
const postsNextPage = ref(null)
const submitting = ref(false)
const submitError = ref(null)
const editingPostId = ref(null)

function authorInitials(author) {
  if (!author) return '?'
  return (author.full_name || '')
    .split(' ').filter(Boolean)
    .map(w => w[0].toUpperCase()).slice(0, 2).join('')
}

function formatDate(dateStr) {
  return stringToDatetime(dateStr)
}

function canEdit(post) {
  if (!currentUser.value || !post.author) return false
  if (currentUser.value.id !== post.author.id) return false
  return Date.now() - new Date(post.created).getTime() < EDIT_WINDOW_MS
}

function startEdit(post) {
  editingPostId.value = post.id
}

async function fetchPosts() {
  postsLoading.value = true
  postsError.value = false
  try {
    const res = await axios.get(`/api/v1/discussions/threads/${props.thread.id}/posts/`)
    const data = Array.isArray(res.data) ? res.data : (res.data.results ?? [])
    localPosts.value = data
    postsNextPage.value = res.data.next ?? null
  } catch {
    postsError.value = true
  } finally {
    postsLoading.value = false
  }
}

async function loadMore() {
  if (!postsNextPage.value) return
  postsLoading.value = true
  try {
    const res = await axios.get(postsNextPage.value)
    const data = Array.isArray(res.data) ? res.data : (res.data.results ?? [])
    localPosts.value = [...localPosts.value, ...data]
    postsNextPage.value = res.data.next ?? null
  } finally {
    postsLoading.value = false
  }
}

async function submitReply(body) {
  submitting.value = true
  submitError.value = null
  const u = currentUser.value
  const optimistic = {
    id: `opt-${Date.now()}`,
    body,
    author: u ? {
      id: u.id,
      full_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
      avatar_url: null,
    } : null,
    created: new Date().toISOString(),
    modified: new Date().toISOString(),
    is_deleted: false,
    _optimistic: true,
  }
  localPosts.value = [...localPosts.value, optimistic]
  try {
    const res = await axios.post(`/api/v1/discussions/threads/${props.thread.id}/posts/`, { body })
    localPosts.value = localPosts.value.map(p => p.id === optimistic.id ? { ...res.data, _optimistic: false } : p)
  } catch (err) {
    localPosts.value = localPosts.value.filter(p => p.id !== optimistic.id)
    submitError.value = err.response?.data?.detail || 'Failed to post reply.'
  } finally {
    submitting.value = false
  }
}

async function submitEdit(postId, body) {
  submitting.value = true
  submitError.value = null
  try {
    const res = await axios.patch(`/api/v1/discussions/threads/${props.thread.id}/posts/${postId}/`, { body })
    localPosts.value = localPosts.value.map(p => p.id === postId ? res.data : p)
    editingPostId.value = null
  } catch (err) {
    submitError.value = err.response?.data?.detail || 'Failed to save edit.'
  } finally {
    submitting.value = false
  }
}

onMounted(fetchPosts)
</script>

<style scoped>
.posts-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
}

.post-item {
  border: 1px solid var(--gray-200);
  border-radius: 8px;
  margin-bottom: 0.75rem;
  background: white;
  padding: 1rem 1.25rem;
}

.post-item--deleted {
  background: var(--gray-100);
  opacity: 0.7;
}

.post-item--optimistic {
  opacity: 0.6;
}

.post-item__deleted {
  color: var(--text-grey);
  font-size: 0.9rem;
}

.post-item__header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.post-item__avatar { flex-shrink: 0; }

.avatar-img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-initials {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--halsa-blue);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
  font-family: 'Raleway', sans-serif;
}

.post-item__author-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.post-item__author-name { font-weight: 700; font-size: 0.9rem; }
.post-item__timestamp { font-size: 0.78rem; color: var(--text-grey); }
.post-item__edited { font-style: italic; }
.post-item__actions { flex-shrink: 0; }

.post-item__body { padding-left: calc(36px + 0.75rem); }
.post-item__reactions { padding-left: calc(36px + 0.75rem); margin-top: 0.4rem; }
.post-item__edit { padding-left: calc(36px + 0.75rem); }

.error-banner {
  background: #fff5f5;
  border: 1px solid var(--halsa-red);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  color: var(--halsa-red);
  margin-bottom: 0.75rem;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0;
}

.load-more {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.reply-section {
  border-top: 1px solid var(--gray-200);
  padding-top: 1rem;
  margin-top: 0.25rem;
}

@media (max-width: 375px) {
  .post-item { padding: 0.75rem; }
  .post-item__body,
  .post-item__reactions,
  .post-item__edit { padding-left: 0; }
}
</style>
